import React, { useState, useEffect } from "react";
import { useLogin, useNotify } from "react-admin";
import { auth } from "./shared/firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  alpha,
  Chip,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { Logo } from './assets/logo';

const LoginPage = ({ view }) => {
  const login = useLogin();
  const notify = useNotify();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [viewType, setViewType] = useState(view);
  
  // Detect view type from environment variable if not passed as prop
  useEffect(() => {
    if (!viewType) {
      setViewType(process.env.REACT_APP_VIEW || 'admin');
    }
  }, [viewType]);
  
  const isCreditorView = viewType === 'creditor';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // First sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Then call React-Admin login
      await login({ 
        username: email, 
        password,
        userCredential // Pass the Firebase user credential to React-Admin
      });
    } catch (error) {
      console.error("Login error:", error);
      notify(
        error.code === "auth/invalid-credential" 
          ? "Invalid email or password"
          : `Error: ${error.message}`,
        { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 400,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
          backdropFilter: 'blur(10px)',
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.9),
        }}
      >
        <Box 
          sx={{ 
            mb: 4,
            height: 60,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Logo height={60} />
        </Box>
        {isCreditorView && (
          <Chip
            icon={<AccountBalanceIcon />}
            label="Creditor Portal"
            color="primary"
            variant="outlined"
            sx={{ mb: 2 }}
          />
        )}
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            textAlign: 'center',
            mb: 1,
          }}
        >
          {isCreditorView ? 'Creditor Access' : 'Welcome Back'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 4,
            textAlign: 'center',
          }}
        >
          {isCreditorView
            ? 'Sign in to view and monitor case information'
            : 'Sign in to access the admin portal'}
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          <TextField
            type="email"
            label="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: (theme) =>
                  alpha(theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.common.white, 0.8),
              },
            }}
          />
          <TextField
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: (theme) =>
                  alpha(theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.common.white, 0.8),
              },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            fullWidth
            startIcon={!loading && isCreditorView ? <AccountBalanceIcon /> : null}
            sx={{
              mt: 1,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
              '&:hover': {
                boxShadow: (theme) => `0 12px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
              bgcolor: isCreditorView ? 'primary.main' : undefined,
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              isCreditorView ? "Access Creditor Portal" : "Sign In"
            )}
          </Button>
          
          {isCreditorView && (
            <Typography 
              variant="caption" 
              color="textSecondary" 
              align="center" 
              sx={{ mt: 2 }}
            >
              This portal provides read-only access to case information
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginPage;
